import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MarketingNav from '../components/Marketing/MarketingNav';
import MarketingFooter from '../components/Marketing/MarketingFooter';
import PlanCard from '../components/Pricing/PlanCard';
import usePublicStore from '../hooks/usePublicStore';
import { useAuth } from '../contexts/AuthContext';
import { CURRENCY_RATES } from '../utils/currency';

const Pricing = () => {
  const { store, loading, error } = usePublicStore();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState('JOD');

  const atpl = store?.courses?.find((c) => c.code === 'ATPL');
  const dlr = store?.courses?.find((c) => c.code === 'DLR');
  const bundle = store?.bundles?.[0];
  const cta = {
    actionLabel: isAuthenticated ? 'Choose in store' : 'Get started',
    onAction: () => navigate(isAuthenticated ? '/store' : '/register'),
  };

  return (
    <div className="min-h-screen bg-slate-950 text-gray-200">
      <MarketingNav links={[{ href: '/', label: 'Home' }, { href: '/#games', label: 'Screening games' }]} />

      <main className="pt-28 md:pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white">Pricing</h1>
              <p className="mt-2 text-gray-400">Pay once for the period you need. No subscription.</p>
            </div>
            <div className="inline-flex self-start rounded-lg border border-white/10 p-1 sm:self-auto" aria-label="Currency">
              {Object.keys(CURRENCY_RATES).map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  aria-pressed={currency === c}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${currency === c ? 'bg-white text-slate-900' : 'text-gray-400 hover:text-white'}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </header>

          {loading && <div className="mt-10 grid gap-4 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-96 animate-pulse rounded-2xl bg-white/5" />)}</div>}
          {error && <p className="mt-10 text-gray-400">Prices couldn&apos;t be loaded right now. Please refresh the page.</p>}

          {store && (
            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {atpl && (
                <PlanCard
                  title="ATPL theory"
                  blurb="The EASA ATPL theory question bank."
                  bullets={[`${atpl.subject_count} subjects, ${Number(atpl.total_questions).toLocaleString()}+ questions`, 'Practice and timed exam modes', 'Retry wrong answers, track progress']}
                  tiers={atpl.pricing_tiers}
                  currency={currency}
                  {...cta}
                />
              )}
              {dlr && (
                <PlanCard
                  title="DLR & airline screening"
                  blurb="Knowledge tests for airline selection."
                  bullets={[`${dlr.subject_count} subjects, ${Number(dlr.total_questions).toLocaleString()}+ questions`, 'Practice and timed exam modes', 'Pick any of the 7 screening games as add-ons']}
                  tiers={dlr.pricing_tiers}
                  currency={currency}
                  extras
                  {...cta}
                />
              )}
              {bundle && (
                <PlanCard
                  title="All-in-One"
                  blurb="ATPL theory and DLR together."
                  bullets={['Everything in ATPL theory', 'Everything in DLR & airline screening', 'One access period for both']}
                  tiers={bundle.pricing_tiers}
                  currency={currency}
                  featured
                  badge="Both programmes"
                  {...cta}
                />
              )}
            </div>
          )}

          <p className="mt-8 text-sm text-gray-400">
            Pay by CliQ bank transfer in Jordanian dinars, or redeem an access code. Access starts once your transfer is confirmed, usually within 12 hours.
            {' '}<Link to="/refund-policy" className="text-primary-300 hover:text-primary-200">Refund policy</Link>
          </p>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default Pricing;
